import React from 'react';
import { RaterIcon, buildIconColorMap } from './PaginatedPrioritySection';

// Rater lane order — every rater letter keeps a fixed vertical lane (slot) across
// the row so a current marker lines up horizontally with its T1 (`*-trend`)
// marker in another column.
const LANE_ORDER = ['S', 'M', 'P', 'T', 'O'];

// Default current-marker colours come from the SAME map the Top Strengths table
// uses, so the markers are visually identical (RaterIcon + these colours). The
// Appendix uses this default; the priority tables (Top Strengths / Development
// Areas) pass their own `legendKeys`-derived map so the current markers match
// exactly the colours those tables already use.
//
// Computed lazily (not at module load): this module and PaginatedPrioritySection
// import each other, so `buildIconColorMap` may still be `undefined` while this
// module is first evaluated. Deferring the call to render time — when both
// modules are fully loaded — avoids that temporal-dead-zone crash.
let _defaultColorMap = null;
function getDefaultColorMap() {
  if (!_defaultColorMap) {
    _defaultColorMap = buildIconColorMap(LANE_ORDER.map((letter) => ({ letter })));
  }
  return _defaultColorMap;
}
const TREND_COLOR = '#c9c9c9'; // grey for all T1 (*-trend) markers

const NUM_COLUMNS = 6; // Never, Rarely, Occasionally, Sometimes, Usually, Always

// Parse the 6 scale-column arrays into, per rater letter, the current column and
// its `*-trend` column (or null). scalePositions: array[6] of token arrays.
function buildLanes(scalePositions) {
  const current = {};
  const trend = {};
  scalePositions.forEach((tokens, colIdx) => {
    (tokens || []).forEach((tok) => {
      const m = /^([A-Z])(-trend)?$/.exec(String(tok).trim());
      if (!m) return;
      const letter = m[1];
      if (m[2]) trend[letter] = colIdx;
      else current[letter] = colIdx;
    });
  });

  // A lane exists for any letter present as current or trend. Keep fixed order.
  const letters = LANE_ORDER.filter((l) => l in current || l in trend);
  Object.keys({ ...current, ...trend }).forEach((l) => {
    if (!letters.includes(l)) letters.push(l);
  });

  return letters.map((letter) => ({
    letter,
    curCol: letter in current ? current[letter] : null,
    trendCol: letter in trend ? trend[letter] : null,
  }));
}

// Renders the exact same marker as the Top Strengths table (RaterIcon). Current
// markers use the given colour map; T1 (`*-trend`) markers force grey.
function Marker({ letter, variant, colorMap }) {
  const isTrend = variant === 'trend';
  return (
    <RaterIcon
      label={letter}
      colorMap={colorMap}
      background={isTrend ? TREND_COLOR : undefined}
    />
  );
}

// Build the connecting-line class for a given lane at a given column. The line is
// drawn per-cell (half-segment out of each marker cell, full-segment through the
// cells in between) and z-indexed behind the markers, so it visually joins the
// current marker to its T1 marker across the real <td> scale cells.
function lineClassFor(lane, col) {
  const { curCol, trendCol } = lane;
  if (curCol == null || trendCol == null || curCol === trendCol) return '';
  const from = Math.min(curCol, trendCol);
  const to = Math.max(curCol, trendCol);
  if (col > from && col < to) return ' appendix-slot--line-full';
  if (col === from) return ' appendix-slot--line-right';
  if (col === to) return ' appendix-slot--line-left';
  return '';
}

// One lane's slot inside a single column cell: the marker (if any) for this
// lane+column, plus any connecting-line segment passing through.
function LaneSlot({ lane, col, colorMap }) {
  const { letter, curCol, trendCol } = lane;
  const sameColumn = curCol != null && trendCol != null && curCol === trendCol;

  let marker = null;
  if (sameColumn && col === curCol) {
    marker = (
      <span className="appendix-marker-wrap appendix-marker-wrap--ring">
        <Marker letter={letter} variant="current" colorMap={colorMap} />
      </span>
    );
  } else if (col === curCol) {
    marker = (
      <span className="appendix-marker-wrap">
        <Marker letter={letter} variant="current" colorMap={colorMap} />
      </span>
    );
  } else if (col === trendCol) {
    marker = (
      <span className="appendix-marker-wrap">
        <Marker letter={letter} variant="trend" colorMap={colorMap} />
      </span>
    );
  }

  return (
    <div className={`appendix-slot${lineClassFor(lane, col)}`}>
      {marker}
    </div>
  );
}

// Renders the scale portion of ONE behavior row as SIX real <td class="col-scale">
// cells — identical structure to the Top Strengths table, so the columns and
// their blue dividers align with the header via the table engine (no colSpan, no
// pixel math). Each cell stacks the fixed lane slots for its column.
//
// Returns an array of <td> elements; the row spreads them after the category td,
// exactly like PriorityRowCells does. Used by both the Appendix page and — for
// the trend report — the Top Strengths / Development Areas priority tables, which
// pass their own `colorMap` so current markers match the table's own colours.
export function appendixScaleCells(scalePositions, colorMap) {
  const resolvedColorMap = colorMap || getDefaultColorMap();
  const lanes = buildLanes(scalePositions || []);
  const cells = [];
  for (let col = 0; col < NUM_COLUMNS; col += 1) {
    cells.push(
      <td key={col} className="col-scale appendix-scale-cell">
        <div className="appendix-slots">
          {lanes.map((lane) => (
            <LaneSlot key={lane.letter} lane={lane} col={col} colorMap={resolvedColorMap} />
          ))}
        </div>
      </td>
    );
  }
  return cells;
}
